/**
 * [MCV-1724] The FAQ renders in two places that scroll differently, so every class that depends on
 * where the viewport's top edge is comes from this one map.
 *
 * - `public`: the marketing site. The document scrolls underneath the 80px fixed PublicHeader, so
 *   anchors, the sticky "Jump to" bar and the sticky side nav all clear 80px (plus the bar's ~67px
 *   on mobile). Hero and body gutters are local to this public page.
 * - `app`: the signed-in shell, behind the header ?. Only Layout's main column scrolls; the top bar
 *   sits above that column, not over it. So every offset is measured from the column's own top, and
 *   there is no header to clear. It fills Layout's column like every other signed-in page, with no
 *   centered cap, and the hero text lines up with the side nav (MCV-1724, QA 2026-10-05).
 *
 * The strings are written out in full, never composed, because Tailwind finds classes by scanning
 * source text.
 *
 * Never put `overflow-hidden`, or `rounded-*` with overflow, on the FAQ's `main`: it would become
 * the scroll container the sticky navs stick to, and they would stop sticking.
 */
export type FaqLayout = 'public' | 'app';

interface FaqLayoutClasses {
  main: string;
  hero: string;
  /** The hero's text wrapper. */
  heroInner: string;
  body: string;
  /** Distance a jumped-to section keeps from the top edge (scroll-margin-top). */
  sectionScrollMargin: string;
  /** The mobile "Jump to" bar. */
  mobileNav: string;
  /** The desktop side-nav tree. */
  desktopNav: string;
}

export const FAQ_LAYOUT_CLASSES: Record<FaqLayout, FaqLayoutClasses> = {
  public: {
    main: 'bg-white text-left',
    hero: 'bg-white px-4 py-8 sm:px-6 sm:py-24 lg:px-8',
    heroInner: 'mx-auto max-w-3xl text-left',
    body: 'mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-16 lg:px-8',
    sectionScrollMargin: 'scroll-mt-40 lg:scroll-mt-28',
    mobileNav:
      'sticky top-20 z-20 -mx-4 border-b border-grey-800 bg-white/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6 lg:hidden',
    desktopNav: 'hidden max-h-[calc(100vh-7rem)] overflow-y-auto pr-2 lg:sticky lg:top-24 lg:block',
  },
  app: {
    main: 'bg-white text-left',
    // Below sm, Layout supplies px-4 once; -mx-4 on the topic bar bleeds to the column edges.
    hero: 'bg-white px-0 pt-0 pb-4 sm:px-6 sm:pt-8 lg:px-8',
    heroInner: 'max-w-3xl text-left',
    body: 'px-0 pt-0 pb-12 sm:px-6 sm:pt-8 lg:px-8',
    // Mobile: the "Jump to" bar (~67px: py-3 + a 42px button + its border) plus a 13px gap, the
    // same arithmetic as the public 160 (80 header + 67 + 13) minus the header. Desktop: the side
    // nav is beside the content, so only the 32px sticky offset remains.
    sectionScrollMargin: 'scroll-mt-20 lg:scroll-mt-8',
    mobileNav:
      'sticky top-0 z-20 -mx-4 border-b border-grey-800 bg-white/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6 lg:hidden',
    // The scroller is 100vh minus the 72px top bar and, from `lg` up, the ~45px footer (and, while
    // impersonating, the banner above the bar [MCV-1752]). Less the sticky top-8 offset and ~24px
    // of breathing room, that is 11rem.
    desktopNav:
      'hidden max-h-[calc(100vh-11rem-var(--impersonation-banner-height,0px))] overflow-y-auto pr-2 lg:sticky lg:top-8 lg:block',
  },
};
